import { FC, ReactNode, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { css } from '@emotion/react';
import { Box, Flex } from '@rebass/emotion';

import { AppTheme } from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';

import { AlertType, AlertLayout } from './Alert.constants';
import { Container, ActionsContainer, getAlertStyles } from './Alert.styles';
import InfoIcon from '../../icons/InfoIcon';
import SuccessIcon from './SuccessIcon';
import WarningIcon from './WarningIcon';
import ErrorIcon from './ErrorIcon';

export { AlertType, AlertLayout };

export const Alert: FC<AlertProps> = ({
  type = AlertType.Info,
  shouldShake = false,
  layout = AlertLayout.Row,
  actions,
  children,
  ...props
}) => {
  const theme: AppTheme = useTheme();

  const alertStyles = useMemo(() => getAlertStyles(theme, type, shouldShake), [
    type,
    shouldShake,
    theme,
  ]);

  const Icon = useMemo(() => {
    switch (type) {
      case AlertType.Success:
        return SuccessIcon;
      case AlertType.Warning:
        return WarningIcon;
      case AlertType.Error:
        return ErrorIcon;
      default:
        return InfoIcon;
    }
  }, [type]);

  return (
    <Container {...props} role="alert" css={alertStyles}>
      <Icon fill="currentColor" />
      <Flex width="100%" flexDirection={layout}>
        <Box flexGrow={1}>{children}</Box>
        {actions ? (
          <ActionsContainer layout={layout}>{actions}</ActionsContainer>
        ) : null}
      </Flex>
    </Container>
  );
};

const contentMargins = css`
  &:not(:last-child) {
    margin-bottom: ${rem(4)};
  }
`;

export const AlertTitle: FC = styled.div`
  font-weight: 700;
  ${contentMargins}
`;

export const AlertMessage: FC = styled.div`
  color: ${({ theme }) => (theme as AppTheme).colors.black};
  ${contentMargins}
`;

export const AlertAction = styled.div`
  font-size: ${rem(14)};

  & button {
    font: inherit;
    padding: 0;
    color: currentColor;
    border: none;
    background: none;

    &:not(:disabled) {
      cursor: pointer;
    }
  }

  & a {
    font: inherit;
    color: currentColor;
  }
`;

type AlertProps = InferComponentProps<typeof Container> & {
  type?: AlertType;
  layout?: AlertLayout;
  shouldShake?: boolean;
  actions?: ReactNode;
};
